import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { NEW_API_SERVER } from "../components/Auth/AuthGuard";
import { LOGOUT } from "../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    DownOutlined,
    LogoutOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import { toast } from "react-hot-toast";

const AUTH_COOKIE_NAMES = [
    "Authorization",
    "authorization",
    "access_token",
    "refresh_token",
    "token",
];

const AUTH_STORAGE_KEYS = [
    "Authorization",
    "authorization",
    "access_token",
    "refresh_token",
    "token",
    "auth",
    "account",
    "user",
    "persist:auth",
    "persist:account",
    "persist:root",
];

const clearAuthenticationData = () => {
    AUTH_COOKIE_NAMES.forEach((cookieName) => {
        // Remove the cookie using both the default path and the explicit root path.
        Cookies.remove(cookieName);
        Cookies.remove(cookieName, { path: "/" });
    });

    AUTH_STORAGE_KEYS.forEach((storageKey) => {
        localStorage.removeItem(storageKey);
        sessionStorage.removeItem(storageKey);
    });

    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common.authorization;
};

/* Module-level on purpose: a component declared inside another one is a new
   type on every render and would remount (and re-request) the <img>. */
const Avatar = ({ src, initials, size = "md" }) => {
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (
        <span className={`thd-avatar thd-avatar--${size}`}>
            {src && !failed ? (
                <img src={src} alt="" onError={() => setFailed(true)} />
            ) : (
                <span>{initials}</span>
            )}
        </span>
    );
};

/**
 * Account menu that lives at the end of the top bar.
 * The trigger is an avatar chip (avatar only on small screens); the panel opens
 * below it. `compact` forces the avatar-only trigger at any width.
 * The Toaster lives once in the layout, so this component does not mount one.
 */
const TheHeaderDropdown = ({ compact = false }) => {
    const account = useSelector((state) => state.account);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const rootRef = useRef(null);
    const triggerRef = useRef(null);

    const userInformation = useMemo(() => {
        const user = account?.user || account?.currentUser || account?.profile || account || {};
        const firstName = user?.first_name || "";
        const lastName = user?.last_name || "";
        const combinedName = `${firstName} ${lastName}`.trim();

        const displayName =
            user?.full_name ||
            combinedName ||
            user?.username ||
            user?.name ||
            "کاربر سامانه";

        const secondaryText = user?.email || user?.username || "حساب کاربری فعال";
        const avatarSource = user?.avatar || user?.avatar_url || "avatars/a2_192.png";
        const initials = displayName
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join("");

        return {
            displayName,
            secondaryText,
            avatarSource,
            initials: initials || "ک",
        };
    }, [account]);

    // navigating somewhere else closes the panel
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // a closed panel never reopens in the "are you sure?" state
    useEffect(() => {
        if (!open) setConfirming(false);
    }, [open]);

    // outside click + Escape
    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
                if (triggerRef.current) triggerRef.current.focus();
            }
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const logout = async () => {
        if (isLoggingOut) return;

        setIsLoggingOut(true);
        const toastId = toast.loading("در حال خروج امن از سامانه...");
        const authorization = Cookies.get("Authorization") || account?.token;

        try {
            // Ask the backend to invalidate the session as well. Local cleanup still
            // runs when this endpoint is unavailable or returns an error.
            await axios.post(
                `${NEW_API_SERVER()}users/logout`,
                {},
                {
                    withCredentials: true,
                    headers: authorization ? { Authorization: authorization } : {},
                }
            );
        } catch (error) {
            console.warn("Logout endpoint error:", error);
        } finally {
            clearAuthenticationData();
            dispatch({ type: LOGOUT });

            toast.success("با موفقیت از حساب کاربری خارج شدید", {
                id: toastId,
                duration: 1800,
            });

            setOpen(false);
            navigate("/login", { replace: true });
            setIsLoggingOut(false);
        }
    };

    return (
        <div className="thd-root" ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className={`thd-trigger ${open ? "is-open" : ""} ${compact ? "thd-trigger--compact" : ""}`}
                aria-label="باز کردن منوی حساب کاربری"
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls="thd-panel"
                onClick={() => setOpen((v) => !v)}
            >
                <span className="thd-avatar-shell">
                    <Avatar src={userInformation.avatarSource} initials={userInformation.initials} />
                    <span className="thd-online-dot" />
                </span>

                {!compact && (
                    <>
                        <span className="thd-trigger-copy">
                            <strong>{userInformation.displayName}</strong>
                            <small>{userInformation.secondaryText}</small>
                        </span>
                        <DownOutlined className="thd-chevron" />
                    </>
                )}
            </button>

            {open && (
                <div id="thd-panel" className="thd-panel" role="dialog" aria-label="حساب کاربری">
                    <div className="thd-panel-head">
                        <Avatar
                            src={userInformation.avatarSource}
                            initials={userInformation.initials}
                            size="lg"
                        />
                        <div className="thd-user-copy">
                            <strong title={userInformation.displayName}>{userInformation.displayName}</strong>
                            <span title={userInformation.secondaryText}>{userInformation.secondaryText}</span>
                        </div>
                    </div>

                    <div className="thd-panel-body">
                        <div className="thd-row">
                            <span className="thd-row-label">
                                <SafetyCertificateOutlined /> وضعیت نشست
                            </span>
                            <span className="thd-chip thd-chip--ok">
                                <span className="thd-live-dot" />
                                امن و فعال
                            </span>
                        </div>

                        <div className="thd-panel-footer">
                            {confirming ? (
                                <div className="thd-confirm">
                                    <span className="thd-confirm-text">از حساب خارج می‌شوید؟</span>
                                    <button
                                        type="button"
                                        className="thd-btn thd-btn--yes"
                                        onClick={logout}
                                        disabled={isLoggingOut}
                                    >
                                        {isLoggingOut ? (
                                            <>
                                                <span className="thd-spinner" /> در حال خروج...
                                            </>
                                        ) : (
                                            "بله"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="thd-btn thd-btn--no"
                                        onClick={() => setConfirming(false)}
                                        disabled={isLoggingOut}
                                    >
                                        خیر
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="thd-logout-btn"
                                    onClick={() => setConfirming(true)}
                                >
                                    <LogoutOutlined /> خروج از حساب کاربری
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .thd-root {
                    position: relative;
                    flex-shrink: 0;
                    direction: rtl;
                    font-family: inherit;
                }

                /* ---------- trigger ---------- */
                .thd-trigger {
                    max-width: 270px;
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 5px 5px 5px 12px;
                    direction: rtl;
                    cursor: pointer;
                    background: #ffffff;
                    border: 1.5px solid #e5e7eb;
                    border-radius: 14px;
                    font-family: inherit;
                    transition: border-color .18s, background .18s, box-shadow .18s, transform .18s;
                }
                .thd-trigger:hover {
                    border-color: #c4b5fd;
                    box-shadow: 0 8px 20px rgba(124,58,237,.14);
                    transform: translateY(-2px);
                }
                .thd-trigger.is-open {
                    background: #f5f3ff;
                    border-color: #7c3aed;
                    box-shadow: 0 0 0 3px rgba(124,58,237,.12);
                    transform: none;
                }
                .thd-trigger--compact { padding: 4px; }

                .thd-avatar-shell { position: relative; display: inline-flex; flex: 0 0 auto; }

                .thd-avatar {
                    display: grid;
                    place-items: center;
                    flex: 0 0 auto;
                    overflow: hidden;
                    background: linear-gradient(145deg, #7c3aed 0%, #4c1d95 100%);
                    color: #ffffff;
                    font-weight: 800;
                    user-select: none;
                }
                .thd-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .thd-avatar--md { width: 38px; height: 38px; border-radius: 12px; font-size: 14px; }
                .thd-avatar--lg {
                    width: 58px; height: 58px; border-radius: 18px; font-size: 21px;
                    border: 2px solid rgba(255,255,255,.55);
                    box-shadow: 0 8px 22px rgba(30,27,75,.35);
                }

                .thd-online-dot {
                    position: absolute;
                    left: -3px; bottom: -3px;
                    width: 11px; height: 11px;
                    border-radius: 50%;
                    background: #10b981;
                    border: 2px solid #ffffff;
                }
                .thd-trigger.is-open .thd-online-dot { border-color: #f5f3ff; }

                .thd-trigger-copy {
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    text-align: right;
                    line-height: 1.3;
                }
                .thd-trigger-copy strong {
                    max-width: 150px;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                    color: #1e1b4b; font-size: 13px; font-weight: 800;
                }
                .thd-trigger-copy small {
                    max-width: 150px;
                    margin-top: 2px;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                    color: #9ca3af; font-size: 10.5px; font-weight: 600;
                    direction: ltr; text-align: right;
                }
                .thd-chevron {
                    flex-shrink: 0;
                    color: #a78bfa; font-size: 10px;
                    transition: transform .2s ease;
                }
                .thd-trigger.is-open .thd-chevron { transform: rotate(180deg); color: #7c3aed; }

                /* ---------- panel ---------- */
                .thd-panel {
                    position: absolute;
                    top: calc(100% + 12px);
                    left: 0;
                    z-index: 60;
                    width: 308px;
                    background: #ffffff;
                    border: 1.5px solid #ede9fe;
                    border-radius: 22px;
                    box-shadow: 0 22px 60px rgba(49,46,129,.22);
                    overflow: hidden;
                    overflow: clip;
                    transform-origin: top left;
                    animation: thd-pop .22s cubic-bezier(.34,1.56,.64,1);
                }
                @keyframes thd-pop {
                    from { opacity: 0; transform: translateY(-6px) scale(.96); }
                    to   { opacity: 1; transform: none; }
                }

                .thd-panel-head {
                    position: relative;
                    display: flex; align-items: center; gap: 14px;
                    padding: 20px 18px;
                    background: linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%);
                    overflow: hidden;
                }
                .thd-panel-head::after {
                    content: "";
                    position: absolute; left: -46px; top: -58px;
                    width: 150px; height: 150px; border-radius: 50%;
                    background: rgba(255,255,255,.10);
                    pointer-events: none;
                }
                .thd-user-copy {
                    position: relative; z-index: 1;
                    min-width: 0; flex: 1;
                    display: flex; flex-direction: column; align-items: flex-start;
                    line-height: 1.4;
                }
                .thd-user-copy strong {
                    width: 100%;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                    color: #ffffff; font-size: 15px; font-weight: 800;
                }
                .thd-user-copy span {
                    width: 100%;
                    margin-top: 3px;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                    color: #ddd6fe; font-size: 11px; font-weight: 600;
                    direction: ltr; text-align: right;
                }

                .thd-panel-body {
                    padding: 16px;
                    display: flex; flex-direction: column; gap: 14px;
                }

                .thd-row {
                    display: flex; align-items: center; justify-content: space-between; gap: 10px;
                    padding: 10px 12px;
                    background: #faf5ff;
                    border: 1.5px solid #ede9fe;
                    border-radius: 14px;
                }
                .thd-row-label {
                    display: inline-flex; align-items: center; gap: 7px;
                    color: #374151; font-size: 12.5px; font-weight: 700;
                }
                .thd-row-label .anticon { color: #7c3aed; font-size: 14px; }

                .thd-chip {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 4px 11px;
                    border-radius: 20px;
                    font-size: 11.5px; font-weight: 700; white-space: nowrap;
                }
                .thd-chip--ok {
                    background: #d1fae5; color: #065f46; border: 1.5px solid #a7f3d0;
                }
                .thd-live-dot {
                    width: 7px; height: 7px; border-radius: 50%;
                    background: #10b981;
                    box-shadow: 0 0 0 0 rgba(16,185,129,.5);
                    animation: thd-pulse 2s ease-out infinite;
                }
                @keyframes thd-pulse {
                    0%   { box-shadow: 0 0 0 0 rgba(16,185,129,.5); }
                    70%  { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
                    100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
                }

                .thd-panel-footer {
                    padding-top: 14px;
                    border-top: 1.5px dashed #e5e7eb;
                }

                /* ---------- logout + two-step confirm ---------- */
                .thd-logout-btn {
                    width: 100%;
                    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                    padding: 11px 14px;
                    background: #fee2e2; color: #b91c1c;
                    border: 1.5px solid #fecaca; border-radius: 12px;
                    font-family: inherit; font-size: 13px; font-weight: 700;
                    cursor: pointer;
                    transition: background .18s, color .18s, border-color .18s, box-shadow .18s, transform .18s;
                }
                .thd-logout-btn:hover {
                    background: #ef4444; color: #ffffff; border-color: #ef4444;
                    box-shadow: 0 8px 20px rgba(239,68,68,.34);
                    transform: translateY(-2px);
                }

                .thd-confirm {
                    display: flex; align-items: center; gap: 8px;
                    padding: 8px 8px 8px 10px;
                    background: #fff7ed; border: 1.5px solid #fed7aa; border-radius: 12px;
                    animation: thd-fade .25s ease;
                }
                @keyframes thd-fade { from { opacity: 0; } to { opacity: 1; } }
                .thd-confirm-text {
                    flex: 1; min-width: 0;
                    color: #92400e; font-size: 12.5px; font-weight: 700;
                }
                .thd-btn {
                    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
                    padding: 7px 14px;
                    border-radius: 10px; border: 1.5px solid transparent;
                    font-family: inherit; font-size: 12.5px; font-weight: 700;
                    cursor: pointer;
                    transition: background .18s, box-shadow .18s, transform .18s;
                }
                .thd-btn:disabled { opacity: .65; cursor: not-allowed; }
                .thd-btn--yes { background: #ef4444; color: #ffffff; box-shadow: 0 4px 12px rgba(239,68,68,.28); }
                .thd-btn--yes:hover:not(:disabled) { background: #dc2626; box-shadow: 0 8px 20px rgba(239,68,68,.4); transform: translateY(-2px); }
                .thd-btn--no { background: #ffffff; color: #374151; border-color: #e5e7eb; }
                .thd-btn--no:hover:not(:disabled) { background: #f3f4f6; }

                .thd-spinner {
                    width: 13px; height: 13px; display: inline-block;
                    border: 2px solid rgba(255,255,255,.4); border-top-color: #ffffff;
                    border-radius: 50%;
                    animation: thd-spin .7s linear infinite;
                }
                @keyframes thd-spin { to { transform: rotate(360deg); } }

                /* ---------- quality floor ---------- */
                .thd-root :focus-visible { outline: 2.5px solid #7c3aed; outline-offset: 2px; }
                .thd-root .thd-btn--yes:focus-visible { outline-color: #b91c1c; }

                @media (max-width: 720px) {
                    .thd-trigger { padding: 4px; }
                    .thd-trigger-copy, .thd-chevron { display: none; }
                    .thd-panel { width: min(308px, calc(100vw - 24px)); }
                }

                @media (prefers-reduced-motion: reduce) {
                    .thd-root *, .thd-root *::before, .thd-root *::after {
                        animation-duration: .01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .01ms !important;
                    }
                    .thd-trigger:hover, .thd-logout-btn:hover, .thd-btn--yes:hover:not(:disabled) { transform: none; }
                }
            `}</style>
        </div>
    );
};

export default TheHeaderDropdown;